import { min } from 'lodash';
import { IShape } from '@/utils/collision-kit';
import { IPoint, StageType } from '@/version-types';
import { CoreAPI } from '../../core';
import { getStageState } from '../states/stage-state';

export const getPreviewModeWrapperId = (identity: string) => `preview-${identity}`;

export const checkIsLongImageMode = (stageType: StageType) => {
    return stageType !== StageType.SINGLE;
};

export const getScaleNode = (identity: string) => {
    return document.querySelector(`#${identity} .l-scale`) as HTMLDivElement;
};

export const getViewportNode = (identity: string) => {
    return document.querySelector(`[data-editor-id="${identity}"]`) as HTMLDivElement;
};

/** 获取编辑器视口节点的 Rect */
export const getViewportRect = (identity: string) => {
    const viewportNode = getViewportNode(identity);
    return (
        viewportNode?.getBoundingClientRect() || {
            left: 0,
            top: 0,
            width: 0,
            height: 0,
        }
    );
};

/** 将浏览器坐标转换为编辑器视口坐标 */
export const convertToViewportPoint = (identity: string, point: IPoint) => {
    const viewportRect = getViewportRect(identity);
    return {
        x: point.x - viewportRect.left,
        y: point.y - viewportRect.top,
    };
};
/**
 * 获取事件元素的 data-type
 *
 * @param target - 目标元素对象
 */
export function getTargetType(target: any) {
    return target?.getAttribute('data-type') || '';
}

/**
 * 获取操作类型
 *
 * @param target - 目标元素对象
 */
export function getOperateType(target: any) {
    return target?.getAttribute('data-operate-type');
}

/**
 * 获取操作方向
 *
 * @param target - 目标元素对象
 */
export function getOperateDirection(target: any) {
    return target?.getAttribute('data-direction');
}

/**
 * 获取图层 ID
 *
 * @param identity - 选择器标识 ID
 * @param target - 目标元素对象
 */
export function getLayerId(identity: string, target: any) {
    const layerId = target?.getAttribute('data-layer-id');
    if (!layerId) return;
    const layer = document.querySelector(`#${identity} [data-layer-id="${layerId}"]`);
    if (!layer) return;
    return layerId;
}

/**
 * 获取渲染舞台节点位置
 *
 * @param identity - 选择器标识 ID
 */
export const getScaleNodePosition = (identity: string) => {
    const scaleNode = getScaleNode(identity);
    if (!scaleNode) return { left: 0, top: 0 };
    const { left, top } = scaleNode.getBoundingClientRect();
    return { left, top };
};

/**
 * 根据鼠标Y位置获取舞台索引
 *
 * @param identity - 选择器标识 ID
 * @param y - Y 坐标
 */
export const getStageIndexByMousePointY = (identity: string, y: number) => {
    const stageType = getStageState(identity).stageType;
    if (!checkIsLongImageMode(stageType)) {
        return getStageState(identity).activeStageIndex;
    }
    const scale = getStageState(identity).scale;
    const sizeList = CoreAPI(identity).getSizeList(scale);
    let { top } = getScaleNodePosition(identity);
    for (let i = 0; i < sizeList.length; i++) {
        const { height } = sizeList[i];
        if (y >= top && y <= top + height) {
            return i;
        }
        top += height;
    }
    return 0;
};

/**
 * 修正舞台索引, 单画直接返回 0
 *
 * @param identity - 选择器标识 ID
 * @param stageIndex - 舞台序号
 */
const fixStageIndex = (identity: string, stageIndex: number) => {
    const stageType = getStageState(identity).stageType;
    if (checkIsLongImageMode(stageType)) {
        return stageIndex;
    }
    return 0;
};

/**
 * 获取舞台视图大小
 *
 * @param identity - 选择器标识 ID
 * @param stageIndex - 舞台序号
 */
export const getStageRect = (identity: string, stageIndex: number) => {
    const oldStageIndex = stageIndex;
    stageIndex = fixStageIndex(identity, stageIndex);
    const scale = getStageState(identity).scale;
    const sizeList = CoreAPI(identity).getSizeList(scale);
    let { top, left } = getScaleNodePosition(identity);
    for (let i = 0; i < sizeList.length; i++) {
        if (stageIndex === i) break;
        const { height } = sizeList[i];
        top += height;
    }
    return {
        top,
        left,
        width: sizeList[oldStageIndex]?.width ?? 0,
        height: sizeList[oldStageIndex]?.height ?? 0,
    };
};

/** 检查点是否在舞台内 */
export const checkPointInStage = (identity: string, point: IPoint, stageIndex: number) => {
    const oldStageIndex = stageIndex;
    stageIndex = fixStageIndex(identity, stageIndex);
    const rect = getStageRect(identity, oldStageIndex);
    if (
        point.x >= rect.left &&
        point.y >= rect.top &&
        point.x <= rect.width + rect.left &&
        point.y <= rect.top + rect.height
    ) {
        return checkPointInViewport(identity, point);
    }
    return false;
};

/**
 * 计算点是否在事件层
 *
 * @param identity - 选择器标识 ID
 * @param point - 坐标点
 */
export const checkPointInViewport = (identity: string, point: IPoint) => {
    const viewportNode = getViewportNode(identity);
    const rect = viewportNode?.getBoundingClientRect() || {
        left: 0,
        top: 0,
        width: 0,
        height: 0,
    };
    if (
        point.x >= rect.left &&
        point.y >= rect.top &&
        point.x <= rect.width + rect.left &&
        point.y <= rect.top + rect.height
    ) {
        return true;
    }
    return false;
};

/** 将鼠标坐标转换为舞台dom坐标 */
export const convertToStagePoint = (identity: string, point: IPoint, stageIndex: number) => {
    stageIndex = fixStageIndex(identity, stageIndex);
    const { stageLeft, stageTop } = getStageOffset(identity, stageIndex);
    return {
        x: point.x - stageLeft,
        y: point.y - stageTop,
    };
};

/** 将鼠标坐标转换为图层坐标 */
export const convertToLayerPoint = (identity: string, point: IPoint, stageIndex: number) => {
    stageIndex = fixStageIndex(identity, stageIndex);
    const scale = getStageState(identity).scale;
    const { stageLeft, stageTop } = getStageOffset(identity, stageIndex);
    return {
        x: (point.x - stageLeft) / scale,
        y: (point.y - stageTop) / scale,
    };
};

/**
 * 获取指定舞台的舞台节点位置
 *
 * @param identity - 选择器标识 ID
 * @param stageIndex - 舞台序号
 */
export const getStageNodePosition = (identity: string, stageIndex: number) => {
    stageIndex = fixStageIndex(identity, stageIndex);
    const scale = getStageState(identity).scale;
    const sizeList = CoreAPI(identity).getSizeList(scale);
    let { top, left } = getScaleNodePosition(identity);
    for (let i = 0; i < sizeList.length; i++) {
        const { height } = sizeList[i];
        if (stageIndex === i) {
            return { top, left };
        }
        top += height;
    }
    return { top: 0, left: 0 };
};

/**
 * 获取指定舞台的舞台节点相对舞台节点位置
 *
 * @param identity - 选择器标识 ID
 * @param stageIndex - 舞台序号
 * @param needPosition - 是否需要计算缩放信息
 */
export const getStageOffset = (identity: string, stageIndex: number, needPosition = false) => {
    stageIndex = fixStageIndex(identity, stageIndex);
    const scalePosition = needPosition ? getScaleNodePosition(identity) : { left: 0, top: 0 };
    const stagePosition = getStageNodePosition(identity, stageIndex);
    return {
        left: stagePosition.left - scalePosition.left,
        top: stagePosition.top - scalePosition.top,
        stageLeft: stagePosition.left,
        stageTop: stagePosition.top,
    };
};

/**
 * 获取一个点的选区
 *
 * @param identity - 选择器标识 ID
 * @param point - 坐标点
 */
export const getPointRect = (identity: string, point: IPoint) => {
    const { x, y } = point;
    const startPoint = convertDocumentPointToNode(identity, point);
    const endPoint = convertDocumentPointToNode(identity, {
        x: x + 1,
        y: y + 1,
    });
    return getMouseMoveRect(startPoint, endPoint);
};

/**
 * 获取鼠标选择框
 *
 * @param startPoint - 起始点
 * @param endPoint - 结束点
 */
export const getMouseMoveRect = (startPoint: IPoint, endPoint: IPoint): IShape => {
    return {
        width: Math.abs(endPoint.x - startPoint.x) || 1,
        height: Math.abs(endPoint.y - startPoint.y) || 1,
        left: min([startPoint.x, endPoint.x]) || 0,
        top: min([startPoint.y, endPoint.y]) || 0,
        rotate: 0,
    } as IShape;
};

/**
 * 将文档坐标转换为节点坐标
 *
 * @param identity - 选择器标识 ID
 * @param point - 坐标点
 * @param destNode - 节点
 */
export const convertDocumentPointToNode = (identity: string, point: IPoint, destNode?: HTMLElement | null): IPoint => {
    const rect = destNode ? destNode.getBoundingClientRect() : getScaleNodePosition(identity);
    return {
        x: point.x - (rect?.left || 0),
        y: point.y - (rect?.top || 0),
    };
};
